import { ArrowLeft, ExternalLink } from 'lucide-react';
import { useDocumentViewUrl } from '@/api/documents';
import { LoadingSpinner } from '@/components/shared/loading-spinner';

// #28/#23/#30 — Visor inline de documentos: consume /documents/:id/view y muestra el
// contenido en el mismo ambiente (iframe PDF / imagen), en vez de descargarlo o abrir
// una URL almacenada vencida. Office llega ya convertido a PDF desde el backend.
export function DocumentViewer({
  documentId,
  title,
  onClose,
}: {
  documentId: number;
  title: string;
  onClose: () => void;
}) {
  const { data, isLoading, isError } = useDocumentViewUrl(documentId);

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-background/95 backdrop-blur-sm">
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="truncate text-sm font-semibold text-foreground">{title}</h2>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-accent"
        >
          <ArrowLeft className="h-4 w-4" />
          Volver
        </button>
      </div>

      <div className="relative flex-1 overflow-auto p-4">
        {isLoading && <LoadingSpinner className="absolute inset-0 z-10" />}

        {isError && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <p className="text-sm text-destructive">No se pudo cargar el documento.</p>
          </div>
        )}

        {data?.kind === 'pdf' && (
          <iframe
            src={data.url}
            title={title}
            className="mx-auto h-full w-full max-w-4xl rounded-lg"
          />
        )}

        {data?.kind === 'image' && (
          <img
            src={data.url}
            alt={title}
            className="mx-auto max-h-full max-w-full rounded-lg object-contain"
          />
        )}

        {data && (data.kind === 'external' || data.kind === 'download') && (
          <div className="flex flex-col items-center justify-center py-20 text-center">
            <p className="text-sm text-muted-foreground">
              Este documento se abre en una pestaña nueva.
            </p>
            <a
              href={data.url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            >
              <ExternalLink className="h-4 w-4" />
              Abrir documento
            </a>
          </div>
        )}
      </div>
    </div>
  );
}
